"use client";

import React, { CSSProperties, useRef, useState } from "react";
import { arc, pie } from "d3";
import { DeckCard } from "modules/decks/model/deck";
import { cn } from "utils/utils";
import { createPortal } from "react-dom";
import { ManaColorEnum } from "modules/cards/model/card";

export interface PieChartDataItem {
  name: string;
  value: number;
  raw?: any;
  color?: string;
  itemStyle?: {
    color?: string;
  };
}

interface PieArcDatum<T> {
  data: T;
  value: number;
  index: number;
  startAngle: number;
  endAngle: number;
  padAngle: number;
}

export interface TooltipContent {
  title?: string;
  items?: { label: string; value?: string | number; color?: string }[];
}

export interface RosenPieChartProps {
  data: PieChartDataItem[];
  tooltip?: {
    cards?: DeckCard[];
    filterFunc?: (d: any) => (c: DeckCard) => boolean;
    titleFunc?: (d: any) => string;
  };
  tooltipFormatter?: (data: PieChartDataItem) => TooltipContent;
  onSliceClick?: (item: PieChartDataItem, index: number) => void;
  centerLabel?: React.ReactNode;
  style?: CSSProperties;
  className?: string;
  height?: number;
  width?: number;
  innerRadius?: number;
  outerRadius?: number;
  showLabels?: boolean;
  showValues?: boolean;
  cornerRadius?: number;
  padAngle?: number;
  colors?: string[];
}

// MTG color mapping - maps ManaColorEnum to actual color values
const MTG_COLOR_MAP: Record<string, string> = {
  [ManaColorEnum.White]: "#f9faf4", // White
  [ManaColorEnum.Blue]: "#0e68ab", // Blue
  [ManaColorEnum.Black]: "#2b2b2b", // Black
  [ManaColorEnum.Red]: "#d3202a", // Red
  [ManaColorEnum.Green]: "#00733e", // Green
  [ManaColorEnum.Colorless]: "#bfbfbf", // Colorless/Gray

  // Common MTG card types
  Creature: "#e74c3c", // Red
  Instant: "#3498db", // Blue
  Sorcery: "#9b59b6", // Purple
  Artifact: "#95a5a6", // Gray
  Enchantment: "#f1c40f", // Yellow
  Planeswalker: "#e67e22", // Orange
  Land: "#2ecc71", // Green
};

export function RosenPieChart({
  data,
  tooltip,
  tooltipFormatter,
  onSliceClick,
  centerLabel,
  style,
  className = "",
  height = 300,
  width = 300,
  innerRadius = 50,
  outerRadius = 100,
  showLabels = true,
  showValues = true,
  cornerRadius = 4,
  padAngle = 0.02,
  colors,
}: RosenPieChartProps) {
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    content: TooltipContent | null;
    x: number;
    y: number;
  }>({
    visible: false,
    content: null,
    x: 0,
    y: 0,
  });

  const chartRef = useRef<HTMLDivElement>(null);

  // Default Tailwind CSS color classes as fallback
  const defaultColors = [
    "text-primary",
    "text-secondary",
    "text-success",
    "text-warning",
    "text-error",
    "text-info",
    "text-accent",
  ];

  // Safety check for data
  const safeData = Array.isArray(data)
    ? data.filter((item) => item && typeof item.value === "number")
    : [];

  // Pie layout and arc generator
  const pieLayout = pie<PieChartDataItem>()
    .sort(null)
    .value((d) => d.value)
    .padAngle(padAngle);

  const arcGenerator = arc<PieArcDatum<PieChartDataItem>>()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius)
    .cornerRadius(cornerRadius);

  // For labels - position them at 80% of the outer radius
  const labelRadius = outerRadius * 0.8;
  const arcLabel = arc<PieArcDatum<PieChartDataItem>>()
    .innerRadius(labelRadius)
    .outerRadius(labelRadius);

  const arcs = pieLayout(safeData);

  // Calculate the angle for each slice to determine if labels should be shown
  const computeAngle = (d: PieArcDatum<PieChartDataItem>) => {
    return ((d.endAngle - d.startAngle) * 180) / Math.PI;
  };

  // Minimum angle to display text
  const MIN_ANGLE = 20;

  // Get color for a slice - handles both CSS classes and direct color values
  const getSliceStyle = (dataItem: PieChartDataItem, index: number) => {
    let colorValue;

    // First try to get color from the data item
    if (dataItem.color) colorValue = dataItem.color;
    else if (dataItem.itemStyle?.color) colorValue = dataItem.itemStyle.color;
    // Try to get color based on MTG color enum if raw value is available
    else if (dataItem.raw && MTG_COLOR_MAP[dataItem.raw])
      colorValue = MTG_COLOR_MAP[dataItem.raw];
    // Try to get color based on name for card types
    else if (dataItem.name && MTG_COLOR_MAP[dataItem.name])
      colorValue = MTG_COLOR_MAP[dataItem.name];
    // Fall back to provided colors array or defaults
    else
      colorValue =
        (colors && colors[index % colors.length]) ||
        defaultColors[index % defaultColors.length];

    // Check if the color is a CSS color value or a Tailwind class
    const isColorValue =
      colorValue.startsWith("#") ||
      colorValue.startsWith("rgb") ||
      colorValue.startsWith("hsl");

    if (isColorValue) {
      return { fill: colorValue, className: "" };
    } else {
      return { fill: "currentColor", className: colorValue };
    }
  };

  // Process tooltip content based on the type of tooltip data
  const getTooltipContent = (index: number): TooltipContent => {
    const dataItem = safeData[index];

    // If custom tooltip formatter is provided, use it
    if (tooltipFormatter) {
      return tooltipFormatter(dataItem);
    }

    // If it's a card tooltip
    if (tooltip?.cards && tooltip.filterFunc) {
      const matchingCards = tooltip.cards
        .filter(tooltip.filterFunc(dataItem))
        .sort((a, b) => a.name.localeCompare(b.name));

      return {
        title: tooltip.titleFunc ? tooltip.titleFunc(dataItem) : dataItem.name,
        items: matchingCards.map((card) => ({
          label: card.name,
          value: card.quantity > 1 ? `x${card.quantity}` : undefined,
          color: card.quantity > 1 ? "text-secondary" : undefined,
        })),
      };
    }

    // Default tooltip
    return {
      title: dataItem.name,
      items: [
        {
          label: "Value",
          value: dataItem.value,
        },
      ],
    };
  };

  const handleMouseEnter = (index: number, event: React.MouseEvent) => {
    const content = getTooltipContent(index);
    setTooltipData({
      visible: true,
      content,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (tooltipData.visible) {
      setTooltipData((prev) => ({
        ...prev,
        x: event.clientX,
        y: event.clientY,
      }));
    }
  };

  const handleMouseLeave = () => {
    setTooltipData((prev) => ({
      ...prev,
      visible: false,
    }));
  };

  // Render tooltip
  const renderTooltip = () => {
    if (!tooltipData.visible || !tooltipData.content) return null;

    // Only run on client
    if (typeof document === "undefined") return null;

    const content = tooltipData.content;

    return createPortal(
      <div
        className="border-base-200 bg-base-100 z-9999 fixed min-w-[180px] border p-0 shadow-lg"
        style={{
          left: tooltipData.x + 10,
          top: tooltipData.y - 10,
        }}
      >
        {content.title && (
          <div className="border-base-300 bg-base-200 text-base-content border-b px-3 py-2">
            <h3 className="text-sm font-medium">{content.title}</h3>
          </div>
        )}

        {content.items && content.items.length > 0 ? (
          <ul className="px-2 py-2">
            {content.items.map((item, i) => (
              <li key={i} className="text-base-content py-0.5 text-sm">
                {item.label}
                {item.value && (
                  <span className={cn("ml-1 font-medium", item.color)}>
                    {item.value}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-base-content/70 px-4 py-2 text-sm italic">
            No data available
          </div>
        )}
      </div>,
      document.body
    );
  };

  return (
    <div
      ref={chartRef}
      className={cn("relative mx-auto", className)}
      style={{
        height: `${height}px`,
        width: `${width}px`,
        ...style,
      }}
    >
      <svg
        viewBox={`-${outerRadius} -${outerRadius} ${outerRadius * 2} ${outerRadius * 2}`}
        className="h-full w-full overflow-visible"
      >
        {/* Slices */}
        {arcs.map((d, i) => {
          const sliceStyle = getSliceStyle(d.data, i);
          return (
            <g
              key={i}
              className={cn(
                "transition-opacity duration-200 hover:opacity-90",
                onSliceClick && "cursor-pointer"
              )}
              onMouseEnter={(e) => handleMouseEnter(i, e)}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={() => onSliceClick?.(d.data, i)}
            >
              <path
                d={arcGenerator(d)!}
                className={sliceStyle.className}
                fill={sliceStyle.fill}
              />
            </g>
          );
        })}

        {/* Labels */}
        {showLabels &&
          arcs.map((d, i) => {
            const angle = computeAngle(d);
            if (angle <= MIN_ANGLE) return null;

            const [x, y] = arcLabel.centroid(d);

            return (
              <g key={`label-${i}`} className="pointer-events-none">
                {showValues && (
                  <text
                    x={x * 0.6}
                    y={y * 0.6}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="fill-base-content text-xs font-semibold"
                  >
                    {d.data.value}
                  </text>
                )}
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-base-content text-xs font-medium"
                >
                  {d.data.name}
                </text>
              </g>
            );
          })}

        {/* Center label in donut hole */}
        {centerLabel && (
          <foreignObject
            x={-innerRadius}
            y={-innerRadius}
            width={innerRadius * 2}
            height={innerRadius * 2}
          >
            <div className="flex h-full w-full items-center justify-center">
              {centerLabel}
            </div>
          </foreignObject>
        )}
      </svg>

      {/* Render tooltip through portal */}
      {tooltipData.visible && renderTooltip()}
    </div>
  );
}
